import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import { getUnseenCount } from "../api/messages"
import {
  MessageCircle,
  ShoppingCart,
  User,
  Menu,
  X,
  Flag,
  ChevronDown,
} from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import "../css/NavbarLog.css";

/* ----------------------------------------------------------------
   Static config — single source of truth for every nav link.
   Adding a route means adding one line here, not editing four
   separate <ul> blocks in the markup.
   ---------------------------------------------------------------- */

const PRIMARY_LINKS = [
  { to: "", en: "Home", fa: "خانه" },
  { to: "gallery", en: "Gallery", fa: "گالری" },
  { to: "about", en: "About", fa: "درباره ما" },
  { to: "contact", en: "Contact", fa: "تماس" },
];

const AUTH_MENU_LINKS = [
  { to: "messages", en: "Messages", fa: "پیام‌ها" },
  { to: "cart", en: "View Cart", fa: "سبد خرید" },
  { to: "profile", en: "Profile", fa: "حساب کاربری" },
  { to: "productlist/manage", en: "Manage Articles", fa: "آگهی‌های من" },
  { to: "review/open-orders", en: "View Orders", fa: "سفارش‌ها" },
  { to: "wishlist", en: "Wishlist", fa: "علاقه‌مندی‌ها" },
];

const PROFILE_MENU_LINKS = [
  { to: "profile", en: "Profile", fa: "حساب کاربری" },
  { to: "productlist/manage", en: "Manage Articles", fa: "آگهی‌های من" },
  { to: "wishlist", en: "Wishlist", fa: "علاقه‌مندی‌ها" },
  { to: "review/open-orders", en: "View Orders", fa: "سفارش‌ها" },
];

/* Small helper so JSX doesn't repeat ternaries everywhere */
const tr = (lang, en, fa) => (lang === "en" ? en : fa);

function useDismissableMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  return { open, setOpen, ref };
}

const NavBar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang: routeLang } = useParams();
  const [consoleError, setConsoleError] = useState("");
  const [lang, setLangState] = useState("en");
  const [mobileOpen, setMobileOpen] = useState(false);
  const profileMenu = useDismissableMenu();
  const accountMenu = useDismissableMenu(); // mobile "more" menu
  const langMenu = useDismissableMenu();

  /* ---- language detection / persistence ---- */
  useEffect(() => {
    if (routeLang === "fa" || routeLang === "en") {
      setLangState(routeLang);
      return;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setLangState(timezone === "Asia/Tehran" ? "fa" : "en");
  }, [routeLang]);

  const switchLang = useCallback(
    (nextLang) => {
      setLangState(nextLang);
      langMenu.setOpen(false);
      navigate(`/${nextLang}`);
    },
    [navigate, langMenu]
  );

  /* ---- auth ---- */
  const handleLogout = useCallback(async () => {
    await logout();
    document.cookie = "sessionid=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    navigate(`/${lang}`);
    window.location.reload();
  }, [logout, navigate, lang]);
  

  /* ---- Message Count ---- */
  const [ unseenCount, setUnseenCount] = useState(0)

  useEffect (() => {
    if (user) {
      getUnseenCount()
      .then((res) => {
        setUnseenCount(res.data.unseen)
      })
      .catch((err) => setConsoleError("Error fetching unseen messages:", err));
    }
  }, [user])


  /* close the mobile drawer whenever the viewport grows back to desktop */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 860) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const linkPath = useMemo(
    () => (to) => (to ? `/${lang}/${to}` : `/${lang}`),
    [lang]
  );

  return (
    <nav className="av-navbar">
      <Link
        to={linkPath("")}
        className="av-navbar-logo"
        onClick={() => setMobileOpen(false)}
      >
        BidZone
      </Link>

      {/* ---- primary links: collapse into drawer below 860px ---- */}
      <ul className={`av-navbar-links ${mobileOpen ? "is-open" : ""}`}>
        {PRIMARY_LINKS.map((item) => (
          <li key={item.to || "home"}>
            <Link to={linkPath(item.to)} onClick={() => setMobileOpen(false)}>
              {tr(lang, item.en, item.fa)}
            </Link>
          </li>
        ))}

        {/* auth-only links also live in the drawer on mobile */}
        {isAuthenticated && (
          <li className="av-navbar-links-mobile-only">
            <ul className="av-navbar-sublinks">
              {AUTH_MENU_LINKS.map((item) => (
                <li key={item.to}>
                  <Link to={linkPath(item.to)} onClick={() => setMobileOpen(false)}>
                    {tr(lang, item.en, item.fa)}
                  </Link>
                </li>
              ))}
              <li>
                <button type="button" className="av-link-btn" onClick={handleLogout}>
                  {tr(lang, "Log out", "خروج")}
                </button>
              </li>
            </ul>
          </li>
        )}

        {!isAuthenticated && (
          <li className="av-navbar-links-mobile-only">
            <div className="av-mobile-auth-buttons">
              <Link to={linkPath("register")} onClick={() => setMobileOpen(false)}>
                <button className="av-btn av-btn--ghost" type="button">
                  {tr(lang, "Register", "ثبت نام")}
                </button>
              </Link>
              <Link to={linkPath("login")} onClick={() => setMobileOpen(false)}>
                <button className="av-btn av-btn--solid" type="button">
                  {tr(lang, "Login", "ورود")}
                </button>
              </Link>
            </div>
          </li>
        )}
      </ul>

      <div className="av-navbar-right">
        {isAuthenticated ? (
          <>
            {/* quick icons — visible from small-tablet width up */}
            <div className="av-navbar-icons">
              <Link to={linkPath("messages")} title={tr(lang, "Messages", "پیام‌ها")} style={{textDecoration:'none'}}>
                <MessageCircle size={20} />
                {unseenCount > 0 && (
                  unseenCount < 100 ? (
                    <span
                    style={{
                      position:'relative',
                      top: 8,
                      left: -5,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#ff4d4f',
                      color: '#fff',
                      fontSize: 12,
                      lineHeight: '18px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                    > {unseenCount} </span>
                  ) : <span style={{
                      position:'relative',
                      top: 8,
                      left: -5,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#ff4d4f',
                      color: '#fff',
                      fontSize: 12,
                      lineHeight: '12px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}>...</span>
                )}
              </Link>
              <Link to={linkPath("cart")} title={tr(lang, "View Cart", "سبد خرید")}>
                <ShoppingCart size={20} />
              </Link>

              <div className="av-dropdown" ref={profileMenu.ref}>
                <button
                  type="button"
                  className="av-icon-btn"
                  title={tr(lang, "Profile", "حساب کاربری")}
                  aria-haspopup="true"
                  aria-expanded={profileMenu.open}
                  onClick={() => profileMenu.setOpen((v) => !v)}
                >
                  <User size={20} />
                </button>
                {profileMenu.open && (
                  <ul className="av-dropdown-menu">
                    {PROFILE_MENU_LINKS.map((item) => (
                      <li key={item.to}>
                        <Link to={linkPath(item.to)} onClick={() => profileMenu.setOpen(false)}>
                          {tr(lang, item.en, item.fa)}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <button type="button" className="av-link-btn" onClick={handleLogout}>
                        {tr(lang, "Log out", "خروج")}
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>

            <span className="av-navbar-username">{user?.username}</span>
          </>
        ) : (
          <div className="av-navbar-auth-buttons">
            <Link to={linkPath("register")}>
              <button className="av-btn av-btn--ghost" type="button">
                {tr(lang, "Register", "ثبت نام")}
              </button>
            </Link>
            <Link to={linkPath("login")}>
              <button className="av-btn av-btn--solid" type="button">
                {tr(lang, "Login", "ورود")}
              </button>
            </Link>
          </div>
        )}

        {/* language switcher */}
        <div className="av-dropdown" ref={langMenu.ref}>
          <button
            type="button"
            className="av-icon-btn"
            title={tr(lang, "Language", "زبان")}
            aria-haspopup="true"
            aria-expanded={langMenu.open}
            onClick={() => langMenu.setOpen((v) => !v)}
          >
            <Flag size={20} />
            <ChevronDown size={14} className="av-chevron" />
          </button>
          {langMenu.open && (
            <ul className="av-dropdown-menu av-dropdown-menu--lang">
              <li>
                <button type="button" onClick={() => switchLang("fa")}>
                  فارسی
                </button>
              </li>
              <li>
                <button type="button" onClick={() => switchLang("en")}>
                  English
                </button>
              </li>
            </ul>
          )}
        </div>

        <ThemeToggle />

        {/* hamburger — only rendered/visible under the drawer breakpoint */}
        <button
          type="button"
          className="av-navbar-burger"
          aria-label={tr(lang, "Open menu", "باز کردن منو")}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </nav>
  );
};

export default NavBar;
